// PEOPLE THE FILE MUST KEEP (v1_800).
//
// A person the client confirmed in the conversation (a director named in a
// follow up, a UBO the documents never listed) or a person the officer added by
// hand existed only in the board itself, so every rebuild kept them only if its
// own merge happened to. Several did not: the register build replaced the UBOs,
// the identity reconciliation re-derived the verification list from directors
// and UBOs alone, and the model rewrite of the conversation merge could simply
// leave a person out. The confirmation walk (24/09/26) lost a second director
// the client had confirmed; the Reprocess rebuilt the board without her and she
// was never screened.
//
// This module is the durable record and the one carry. collected_data
// .carriedPeople holds each such person (add only); carryPeople puts every one
// back on a rebuilt board unless the officer removed them (collected_data
// .partyRemovals, the record the removal controls already write). The three
// rebuild paths (documents first processing and Reprocess, interview submit,
// officer regenerate) and the server's save of a regenerated report all call
// carryPeople before they save. Pure; no I/O.
import { samePerson, sameEntryPerson } from './person-name-match.js';
import { isVerificationSubject, isVerificationListEntry, isNonPartyRole, nonPartyReason, subThresholdHolderOnly, statedShare, sourceRole, applicantNamesFromBoard } from './verification-parties.js';
import { caseOwnershipThreshold } from './ownership-threshold.js';

// Where each kind of person lives on the board.
const LIST_OF = { director: 'directors', ubo: 'ubos', signatory: 'authorisedSignatories', verification: 'sumsubRequired' };

export const carriedKey = (name) => String(name || '').toLowerCase().replace(/[^a-z]/g, '').slice(0, 60);

const nameOf = (p) => (p && typeof p === 'object') ? p.name : String(p || '').split(' — ')[0];

// v1_810: THE CARRY GATE, one rule for every carry on every path (carryPeople
// and conversationPeople here, dfCarryPeople in App.jsx, carryPeopleForward in
// the dashboard). A person is carried onto a list only when they belong on it:
//   directors, UBOs, signatories: never a person the role marks as a non party
//     (a certifying signatory, the registered agent's officer, an issuing
//     authority signatory). Before v1_810 only signatories were tested, so a
//     signatory promoted into directors once was carried back on every rebuild
//     (Keith Bliss and Amy R. Fountain, Bloxcross, production 25/09/26);
//   UBOs: never a holder with a stated share below the threshold (a shareholder,
//     not a beneficial owner). An unknown share is carried;
//   the verification list: a verification subject whose only basis is not a sub
//     threshold shareholding (Shareholder 3 at 5.61 percent, carried from the
//     conversation, same case).
export function carryAllowed(list, entry, applicantNames = [], threshold = 25) {
  const e = (entry && typeof entry === 'object') ? entry : { name: nameOf(entry) };
  if (list === 'sumsubRequired') return isVerificationListEntry(e, applicantNames, threshold);
  if (isNonPartyRole(e.role, applicantNames)) return false;
  if (list === 'ubos' && subThresholdHolderOnly(e, threshold)) return false;
  return true;
}

// The kind a role asks for, for a person found only on the verification list.
export function kindForRole(role) {
  const r = String(role || '').toLowerCase();
  if (/\bubo\b|beneficial owner/.test(r)) return 'ubo';
  if (/director/.test(r)) return 'director';
  return 'verification';
}

// People on the merged board that the documents only board did not have: the
// ones the conversation brought. Directors, UBOs and signatories by their list;
// a verification subject on the verification list alone (the merge can place a
// confirmed director there and nowhere else, as in the confirmation walk,
// 24/09/26) by the kind its role asks for. A non party on the verification list
// (an issuing authority signatory, a corporate holder) is never recorded.
export function conversationPeople(documentsBoard, mergedBoard, match = samePerson) {
  const out = [];
  const before = documentsBoard || {};
  const after = mergedBoard || {};
  const had = ['directors', 'ubos', 'authorisedSignatories', 'sumsubRequired'].flatMap(l => (before[l] || []).map(nameOf)).filter(Boolean);
  const seen = (nm) => had.some(h => match(h, nm)) || out.some(o => match(o.name, nm));
  const apps = applicantNamesFromBoard(after);
  for (const [kind, list] of [['director', 'directors'], ['ubo', 'ubos'], ['signatory', 'authorisedSignatories']]) {
    for (const p of (after[list] || [])) {
      const nm = nameOf(p);
      if (!nm || seen(nm)) continue;
      if (kind === 'signatory' && !isVerificationSubject(p, apps)) continue;
      if (!carryAllowed(list, p, apps, caseOwnershipThreshold(after))) continue;
      out.push({ kind, name: nm, entry: (p && typeof p === 'object') ? { ...p } : { name: nm } });
    }
  }
  for (const p of (after.sumsubRequired || [])) {
    const nm = nameOf(p);
    if (!nm || seen(nm)) continue;
    if (!carryAllowed('sumsubRequired', typeof p === 'object' ? p : { name: nm }, apps, caseOwnershipThreshold(after))) continue;
    out.push({ kind: kindForRole(p && p.role), name: nm, entry: (p && typeof p === 'object') ? { ...p } : { name: nm } });
  }
  return out;
}

// Add people to collected_data.carriedPeople. Add only: an existing record is
// kept, with any new fields filled in (never cleared).
export function recordCarriedPeople(cd, people, { source, at } = {}) {
  const base = (cd && typeof cd === 'object') ? cd : {};
  const cur = { ...((base.carriedPeople && typeof base.carriedPeople === 'object') ? base.carriedPeople : {}) };
  let changed = false;
  for (const p of (people || [])) {
    if (!p || !p.name) continue;
    const key = `${p.kind || 'verification'}:${carriedKey(p.name)}`;
    const prev = cur[key];
    const entry = { ...(prev && prev.entry ? prev.entry : {}), ...(p.entry || {}), name: (prev && prev.entry && prev.entry.name && String(prev.entry.name).length > String(p.name).length) ? prev.entry.name : p.name };
    const src = source || p.source || 'conversation';
    if (!prev) { cur[key] = { kind: p.kind || 'verification', name: entry.name, source: src, at: at || new Date().toISOString(), entry }; changed = true; }
    else if (JSON.stringify(prev.entry) !== JSON.stringify(entry)) { cur[key] = { ...prev, name: entry.name, entry }; changed = true; }
  }
  return changed ? { ...base, carriedPeople: cur } : base;
}

// THE v1_766 CARRY, shared. The people the board already has are kept; each new
// person is added, or merged into the entry for the same person (the longer
// spelling of the name kept). registerDirectors (api/df-process.js) is this with
// the register's replacement rule in front; carryPeople is this for every list.
export function keepAndAdd(boardList, newList, match = samePerson) {
  const kept = (Array.isArray(boardList) ? boardList : []).filter(d => d && (typeof d !== 'object' || d.name)).map(d => (typeof d === 'object' ? { ...d } : d));
  for (const nd of (newList || [])) {
    const ndName = nameOf(nd);
    if (!ndName) continue;
    // v1_852: or one email address with a close spelling of the name.
    const at = kept.findIndex(d => { const nm = nameOf(d); return nm && (match(nm, ndName) || (typeof d === 'object' && typeof nd === 'object' && sameEntryPerson(d, nd, match))); });
    if (at === -1) { kept.push(nd); continue; }
    if (typeof kept[at] !== 'object') continue;
    kept[at] = { ...kept[at], ...(typeof nd === 'object' ? nd : {}), name: String(kept[at].name).length >= String(ndName).length ? kept[at].name : ndName };
  }
  return kept;
}

// The officer's removals ({ name, at }).
export function removals(cd) {
  const r = (cd && cd.partyRemovals && typeof cd.partyRemovals === 'object') ? cd.partyRemovals : {};
  return Object.values(r).filter(x => x && x.name);
}

// v1_856: THE LISTS A REMOVED PARTY LEAVES. Both removal controls filtered
// directors, UBOs and the identity list only, so an authorised signatory
// (Oksana Belinska, Harbour Quay, production 28/09/26) stayed on the
// signatories list, the identity set rebuilt her from it, and she sat on the
// panel with Send Link after the removal was recorded. Every people list, the
// same four api/_person-match.js applyPartyRemovals filters on a rebuild.
export const PARTY_LISTS = ['directors', 'ubos', 'authorisedSignatories', 'shareholders'];
export function withoutParty(board, name, match = samePerson) {
  const b = (board && typeof board === 'object') ? board : {};
  const gone = (nm) => !!nm && match(nm, name);
  const out = { ...b };
  for (const k of PARTY_LISTS) if (Array.isArray(b[k])) out[k] = b[k].filter(p => !(p && typeof p === 'object' ? gone(p.name) : gone(String(p || ''))));
  if (Array.isArray(b.sumsubRequired)) out.sumsubRequired = b.sumsubRequired.filter(p => !gone(typeof p === 'object' ? (p && p.name) : String(p || '').split(' — ')[0]));
  return out;
}

// A carried person is kept off the board once the officer removed them, as the
// removal controls keep them off every other list (applyPartyRemovals).
export function isRemoved(c, cd, match = samePerson) {
  return removals(cd).some(r => match(r.name, c.name));
}

// Put every carried person back on the board unless removed. Returns the new
// board and the names added (empty when the board already had everyone).
export function carryPeople(board, cd, match = samePerson) {
  const b = (board && typeof board === 'object') ? board : {};
  const carried = Object.values(((cd && cd.carriedPeople) || {})).filter(x => x && x.name && LIST_OF[x.kind]);
  if (!carried.length) return { board: b, added: [] };
  const next = { ...b };
  const added = [];
  const apps = applicantNamesFromBoard(b);
  for (const c of carried) {
    if (isRemoved(c, cd, match)) continue;
    const list = LIST_OF[c.kind];
    // v1_810: a record made before the gate (or from a stale board) is kept in
    // collected_data, add only, but no longer put back where it does not belong.
    if (!carryAllowed(list, { ...(c.entry || {}), name: c.name }, apps, caseOwnershipThreshold(b))) continue;
    const cur = Array.isArray(next[list]) ? next[list] : [];
    if (cur.some(p => { const nm = nameOf(p); return nm && (match(nm, c.name) || (p && typeof p === 'object' && sameEntryPerson(p, { ...(c.entry || {}), name: c.name }, match))); })) continue;
    const entry = { ...(c.entry || {}), name: c.name };
    if (!entry.role) entry.role = c.kind === 'ubo' ? 'Beneficial owner' : c.kind === 'director' ? 'Director' : c.kind === 'signatory' ? 'Authorised signatory' : 'To verify';
    next[list] = keepAndAdd(cur, [entry], match);
    // A carried director or UBO is a verification subject: the identity send
    // reads the verification list, so they go on it too.
    if ((c.kind === 'director' || c.kind === 'ubo') && Array.isArray(next.sumsubRequired) && !next.sumsubRequired.some(p => { const nm = nameOf(p); return nm && match(nm, c.name); })) {
      next.sumsubRequired = [...next.sumsubRequired, { name: c.name, role: entry.role, ...(entry.email ? { email: entry.email } : {}) }];
    }
    added.push(c.name);
  }
  return { board: added.length ? next : b, added };
}

// The add only merge of the keys the carry reads (the carried people, the
// officer's party emails and removals), for a server that receives a whole
// collected_data from a loaded copy (v1_800). carriedPeople: every entry on
// either side is kept (the stored one wins on a clash, it is the older
// record). partyEmails: every entry kept, the later one wins on a clash.
// partyRemovals: every entry kept. Returns the incoming object, merged.
export function unionCarriedKeys(incoming, stored) {
  const inc = (incoming && typeof incoming === 'object') ? incoming : {};
  const cur = (stored && typeof stored === 'object') ? stored : {};
  const out = { ...inc };
  if (cur.carriedPeople || inc.carriedPeople) out.carriedPeople = { ...(inc.carriedPeople || {}), ...(cur.carriedPeople || {}) };
  if (cur.partyEmails || inc.partyEmails) {
    const pe = { ...(cur.partyEmails || {}) };
    for (const [k, v] of Object.entries(inc.partyEmails || {})) {
      if (!pe[k] || String((v && v.at) || '') >= String((pe[k] && pe[k].at) || '')) pe[k] = v;
    }
    out.partyEmails = pe;
  }
  // The officer's removals are what keep a removed person off the board, so a
  // stale copy without them must not clear them either (there is no restore).
  if (cur.partyRemovals || inc.partyRemovals) out.partyRemovals = { ...(inc.partyRemovals || {}), ...(cur.partyRemovals || {}) };
  return out;
}

// ── v1_810: PARTIES CORRECTED OFF THE FILE, NOTED ONCE ──────────────────────
// When a rebuild drops a person the v1_810 rules class as not belonging (a non
// party in directors, UBOs or signatories, or a sub threshold shareholder on the
// UBO or verification list), the officer is told once, by a case note naming who
// and why. collected_data.nonPartyCorrections records each (add only), so the
// next rebuild does not note them again. Only people the rules name are noted;
// ordinary rebuild churn is not. Pure: the caller writes the note and the record.
const LISTS = [['directors', 'directors'], ['ubos', 'UBOs'], ['authorisedSignatories', 'authorised signatories'], ['sumsubRequired', 'identity verification']];
const plain = (t) => String(t == null ? '' : t).replace(/\s*[\u2014\u2013]\s*/g, ', ').replace(/(\w)-(\w)/g, '$1 $2').replace(/\s-\s/g, ', ').replace(/\s+/g, ' ').trim();
export function nonPartyCorrections(priorReport, newReport, cd, match = samePerson) {
  const prior = (priorReport && typeof priorReport === 'object') ? priorReport : {};
  const next = (newReport && typeof newReport === 'object') ? newReport : {};
  const base = (cd && typeof cd === 'object') ? cd : {};
  const done = (base.nonPartyCorrections && typeof base.nonPartyCorrections === 'object') ? base.nonPartyCorrections : {};
  const apps = applicantNamesFromBoard(next).length ? applicantNamesFromBoard(next) : applicantNamesFromBoard(prior);
  const stillOn = (nm) => LISTS.some(([l]) => (next[l] || []).some(p => { const n = nameOf(p); return n && match(n, nm); }));
  const byKey = new Map();
  for (const [list] of LISTS) {
    for (const p of (prior[list] || [])) {
      const e = (p && typeof p === 'object') ? p : { name: nameOf(p) };
      const nm = e.name;
      if (!nm || carryAllowed(list, e, apps, caseOwnershipThreshold(next)) || stillOn(nm)) continue;
      const key = carriedKey(nm);
      if (done[key] || byKey.has(key)) continue;
      const role = list === 'sumsubRequired' ? sourceRole(e.role) : (e.role || '');
      const why = isNonPartyRole(role, apps) ? nonPartyReason(role, apps) : 'a shareholder below the ' + caseOwnershipThreshold(next) + ' percent threshold (' + statedShare(e) + ' percent), so not a beneficial owner and not an identity verification subject';
      byKey.set(key, { name: nm, role, reason: why, lists: [] });
    }
  }
  // Every list the person was on before, since they are on none now.
  for (const r of byKey.values()) {
    for (const [list, label] of LISTS) if ((prior[list] || []).some(p => { const n = nameOf(p); return n && match(n, r.name); })) r.lists.push(label);
  }
  const removed = [...byKey.values()];
  if (!removed.length) return { removed, cd: base, note: null };
  const at = new Date().toISOString();
  const rec = { ...done };
  for (const r of removed) rec[carriedKey(r.name)] = { name: r.name, role: r.role, reason: r.reason, lists: r.lists, at };
  const note = 'Parties corrected off the file on this rebuild. Each was on the file only through a document role or holding that does not make them a party to verify, so they were removed; nothing already completed for them was changed. If any of them is in fact an officer or beneficial owner, add them back with Add person manually on the identity verification panel.\n'
    + removed.map(r => '\u2022 ' + plain(r.name) + (r.role ? ' (' + plain(r.role) + ')' : '') + ': ' + r.reason + '. Removed from ' + (r.lists.length > 1 ? r.lists.slice(0, -1).join(', ') + ' and ' + r.lists[r.lists.length - 1] : r.lists.join('')) + '.').join('\n');
  return { removed, cd: { ...base, nonPartyCorrections: rec }, note };
}

// v1_810: the source non party gate for a rebuilt report outside df-process (the
// officer regenerate), the same test df-process applies to its board
// (dropNonApplicantParties): a person the role marks as a non party is not kept
// in directors, UBOs or authorised signatories. Returns a new report.
export function dropNonParties(report) {
  const r = (report && typeof report === 'object') ? report : {};
  const apps = applicantNamesFromBoard(r);
  const out = { ...r };
  for (const list of ['directors', 'ubos', 'authorisedSignatories']) {
    if (Array.isArray(r[list])) out[list] = r[list].filter(p => !(p && typeof p === 'object' && isNonPartyRole(p.role, apps)));
  }
  return out;
}
